import { useEffect, useMemo, useState } from "react";
import { submitQuestionnaireStep } from "../api/forms";

const DRAFT_KEY = "cre_questionnaire_draft_v2";
const TOTAL_STEPS = 3;

const countries = [
  "United States of America (USA)",
  "Canada",
  "United Kingdom",
  "United Arab Emirates",
  "Australia",
  "Germany",
  "France",
  "Italy",
  "Spain",
  "Netherlands",
  "Switzerland",
  "Sweden",
  "Norway",
  "Denmark",
  "Ireland",
  "Portugal",
  "Poland",
  "Greece",
  "Turkey",
  "Saudi Arabia",
  "Qatar",
  "Kuwait",
  "India",
  "China, People's Republic of",
  "Japan",
  "South Korea",
  "Singapore",
  "Malaysia",
  "Thailand",
  "Indonesia",
  "Philippines",
  "Mexico",
  "Brazil",
  "Argentina",
  "Chile",
  "Colombia",
  "South Africa",
  "Nigeria",
  "Kenya",
  "Egypt",
  "New Zealand",
  "Other",
];

const initialData = {
  firstName: "",
  lastName: "",
  preferredTitle: "",
  cellPhone: "",
  email: "",
  country: "",
  stateOfResidence: "",
  cityOfResidence: "",
  investState: "",
  investCity: "",
  principalRole: "",
  propertyTypesLookingFor: [],
  targetBudget: "",
  cashOrFinance: "",
  mortgagePreApproval: "",
  readyToInvest: "",
  currentlyOwnRentals: "",
  ownedPropertyTypes: [],
  currentManagementStyle: "",
  workStatus: "",
  newInvestmentManagement: "",
  considering1031: "",
  exchange1031Status: "",
  workingWithAgent: "",
  foundUsHow: "",
  additionalInfo: "",
};

const fields = {
  firstName: { label: "First name", required: true },
  lastName: { label: "Last name", required: true },
  preferredTitle: {
    label: "Preferred title",
    type: "select",
    required: true,
    options: ["Mr.", "Mrs.", "Miss.", "Ms."],
  },
  cellPhone: { label: "Cell phone number", required: true, type: "tel" },
  email: { label: "Email address", required: true, type: "email" },
  country: { label: "Country", required: true, type: "select", options: countries },
  stateOfResidence: { label: "State of residence", required: true },
  cityOfResidence: { label: "City of residence", required: true },
  investState: { label: "Which state will you invest in", required: true },
  investCity: { label: "Which city will you invest in", required: true },
  principalRole: {
    label: "Are you the principal",
    type: "select",
    required: true,
    options: [
      "I am the principal investor",
      "I am an employee of the investor",
      "I am a relative of the investor",
      "I am a real-estate agent/broker",
    ],
  },
  propertyTypesLookingFor: {
    label: "What type of investment property are you looking for (Select all that apply)",
    type: "multiselect",
    required: true,
    options: [
      "Multifamily building",
      "Office condo",
      "Office building",
      "Retail condo",
      "Retail plaza",
      "Warehouse condo",
      "Warehouse building",
      "Hospitality (Hotel)",
      "Mobile Home Park",
      "Other",
    ],
  },
  targetBudget: {
    label: "What is your target investment budget",
    type: "select",
    required: true,
    options: [
      "Less than $500k",
      "$500K - $1 Million",
      "$1 Million - $2 Million",
      "$2 Million - $5 Million",
      "More than $5 Million",
    ],
  },
  cashOrFinance: {
    label: "Will you pay cash or finance your investment",
    type: "select",
    required: true,
    options: [
      "I will pay cash and I have the funds ready",
      "I will pay cash but the funds are not ready yet",
      "I will finance it and I have the down payment ready",
      "I will finance it but I don't have the down payment yet",
    ],
  },
  mortgagePreApproval: {
    label: "Have you obtained mortgage pre-approval",
    type: "select",
    required: true,
    options: ["Yes", "No"],
  },
  readyToInvest: {
    label: "Are you ready to invest",
    type: "select",
    required: true,
    options: ["In next 90 days", "In next 6 months", "Later than 6 months"],
  },
  currentlyOwnRentals: {
    label: "Do you currently own rental investment properties",
    type: "select",
    required: true,
    options: ["Yes", "No"],
  },
  ownedPropertyTypes: {
    label: "What types of rental investment properties do you own (Select all that apply)",
    type: "multiselect",
    required: true,
    options: [
      "Single family home",
      "Condominium",
      "Multifamily building",
      "Office condo",
      "Office building",
      "Retail condo",
      "Retail plaza",
      "Warehouse condo",
      "Warehouse building",
      "Hospitality (Hotel)",
      "Mobile Home Park",
      "Other",
    ],
  },
  currentManagementStyle: {
    label: "How are your current real estate investments managed",
    type: "select",
    required: true,
    options: ["I am managing them myself", "I hired a property manager"],
  },
  workStatus: {
    label: "Are you",
    type: "select",
    required: true,
    options: [
      "Retired",
      "Working part-time",
      "Working full time for somebody else",
      "Working full time at my own business",
    ],
  },
  newInvestmentManagement: {
    label: "How do you intend to manage your new investment(s)",
    type: "select",
    required: true,
    options: ["I will manage them myself", "I will hire a property manager"],
  },
  considering1031: {
    label: "Are you considering a 1031 exchange",
    type: "select",
    required: true,
    options: ["Yes", "No", "I dont know what 1031 exchange is"],
  },
  exchange1031Status: {
    label: "What is your 1031 exchange status",
    type: "select",
    required: true,
    options: [
      "My investment property is not listed for sale yet",
      "My investment property is listed but I don't have a contract on it yet",
      "I have a contract on the sale of my investment property",
      "I already sold/closed on the sale of my investment property",
      "I don't know how 1031 Exchange works",
    ],
  },
  workingWithAgent: {
    label: "Are you currently working with a real estate agent/broker in your search",
    type: "select",
    required: true,
    options: ["Yes", "No"],
  },
  foundUsHow: {
    label: "How did you find us (Select one)",
    type: "select",
    required: true,
    options: [
      "Internet search",
      "A friend/relative recommended",
      "Printed advertisement",
      "Online advertisement",
      "From a frig magnet",
      "From the HOA management office",
      "From a real estate agent",
      "Other",
    ],
  },
  additionalInfo: {
    label: "Additional information",
    type: "textarea",
    required: true,
  },
};

const stepFields = {
  1: [
    "firstName",
    "lastName",
    "preferredTitle",
    "cellPhone",
    "email",
    "country",
    "stateOfResidence",
    "cityOfResidence",
  ],
  2: [
    "investState",
    "investCity",
    "principalRole",
    "propertyTypesLookingFor",
    "targetBudget",
    "cashOrFinance",
    "mortgagePreApproval",
    "readyToInvest",
    "currentlyOwnRentals",
    "ownedPropertyTypes",
    "currentManagementStyle",
    "workStatus",
    "newInvestmentManagement",
  ],
  3: [
    "considering1031",
    "exchange1031Status",
    "workingWithAgent",
    "foundUsHow",
    "additionalInfo",
  ],
};

const stepTitles = {
  1: "Step 1: Personal details",
  2: "Step 2: Investment profile",
  3: "Step 3: 1031 exchange and final details",
};

const createLeadId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `lead-${Date.now()}`;
};

async function saveToBackend(payload) {
  try {
    const data = await submitQuestionnaireStep(payload);
    return { ok: true, leadId: data?.leadId, message: data?.message };
  } catch {
    return { ok: false, message: "We could not reach the server, so your draft is still only local." };
  }
}

function Questionnaire() {
  const [leadId, setLeadId] = useState(createLeadId());
  const [formData, setFormData] = useState(initialData);
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(DRAFT_KEY);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);
      if (parsed.formData) setFormData((prev) => ({ ...prev, ...parsed.formData }));
      if (parsed.leadId) setLeadId(parsed.leadId);
      if (parsed.step && parsed.step >= 1 && parsed.step <= TOTAL_STEPS) setStep(parsed.step);
      if (parsed.done) setDone(true);
    } catch {
      localStorage.removeItem(DRAFT_KEY);
    }
  }, []);

  const visibleFields = useMemo(() => {
    return stepFields[step].filter((id) => {
      if (
        id === "mortgagePreApproval" &&
        !String(formData.cashOrFinance || "").toLowerCase().includes("finance")
      ) {
        return false;
      }
      if (id === "ownedPropertyTypes" && formData.currentlyOwnRentals !== "Yes") return false;
      if (id === "exchange1031Status" && formData.considering1031 !== "Yes") return false;
      return true;
    });
  }, [formData.cashOrFinance, formData.considering1031, formData.currentlyOwnRentals, step]);

  const persistDraft = (nextStep = step, completed = done) => {
    localStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({
        leadId,
        formData,
        step: nextStep,
        done: completed,
        updatedAt: new Date().toISOString(),
      })
    );
  };

  const onValueChange = (id, value) => {
    setFormData((prev) => {
      const next = { ...prev, [id]: value };

      if (id === "currentlyOwnRentals" && value !== "Yes") {
        next.ownedPropertyTypes = [];
      }

      if (id === "cashOrFinance" && !String(value || "").toLowerCase().includes("finance")) {
        next.mortgagePreApproval = "";
      }

      if (id === "considering1031" && value !== "Yes") {
        next.exchange1031Status = "";
      }

      return next;
    });
  };

  const onMultiChange = (id, option) => {
    setFormData((prev) => {
      const current = prev[id] || [];
      const exists = current.includes(option);
      return {
        ...prev,
        [id]: exists ? current.filter((entry) => entry !== option) : [...current, option],
      };
    });
  };

  const clearQuestionnaire = (nextStatus = "") => {
    setLeadId(createLeadId());
    setFormData(initialData);
    setStep(1);
    setDone(false);
    setStatus(nextStatus);
    localStorage.removeItem(DRAFT_KEY);
  };

  const onStepSubmit = async (event) => {
    event.preventDefault();
    setStatus("Saving your progress...");

    const isLastStep = step === TOTAL_STEPS;
    const nextStep = isLastStep ? step : step + 1;

    const localLeadId = leadId || createLeadId();
    if (!leadId) setLeadId(localLeadId);

    const payload = {
      leadId: localLeadId,
      step,
      isComplete: isLastStep,
      submittedAt: new Date().toISOString(),
      data: formData,
    };

    const result = await saveToBackend(payload);
    if (result.leadId) setLeadId(result.leadId);

    if (isLastStep) {
      if (result.ok) {
        clearQuestionnaire(result.message || "Submitted successfully. Thank you.");
      } else {
        setDone(true);
        persistDraft(step, true);
        setStatus(result.message || "We could not submit your questionnaire right now.");
      }
      return;
    }

    setStep(nextStep);
    persistDraft(nextStep, false);
    setStatus(
      result.ok
        ? result.message || "Saved. Moving to next step."
        : result.message || "Saved locally. Moving to next step."
    );
  };

  const resetForm = () => {
    clearQuestionnaire();
  };

  const renderField = (id) => {
    const field = fields[id];
    const value = formData[id];

    if (field.type === "select") {
      return (
        <select
          id={id}
          required={field.required}
          value={value}
          onChange={(event) => onValueChange(id, event.target.value)}
          className="rounded-2xl border border-obsidian-300 bg-cream-50 px-4 py-3 text-sm font-medium text-obsidian-900"
        >
          <option value="">PLEASE CHOOSE ONE</option>
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === "multiselect") {
      return (
        <div className="rounded-2xl border border-obsidian-300 bg-cream-50 p-4">
          <div className="grid gap-2 sm:grid-cols-2">
            {field.options.map((option) => (
              <label key={option} className="flex items-center gap-2 text-sm text-obsidian-900">
                <input
                  type="checkbox"
                  checked={value.includes(option)}
                  onChange={() => onMultiChange(id, option)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
          <input
            className="sr-only"
            required={field.required}
            value={value.join(",")}
            onChange={() => {}}
            tabIndex={-1}
          />
        </div>
      );
    }

    if (field.type === "textarea") {
      return (
        <textarea
          id={id}
          required={field.required}
          rows={5}
          value={value}
          onChange={(event) => onValueChange(id, event.target.value)}
          className="rounded-2xl border border-obsidian-300 bg-cream-50 px-4 py-3 text-sm font-medium text-obsidian-900"
        />
      );
    }

    return (
      <input
        id={id}
        type={field.type || "text"}
        required={field.required}
        value={value}
        onChange={(event) => onValueChange(id, event.target.value)}
        className="rounded-2xl border border-obsidian-300 bg-cream-50 px-4 py-3 text-sm font-medium text-obsidian-900"
      />
    );
  };

  return (
    <div className="section-wrap space-y-8 py-10 lg:py-14">
      <section className="rounded-3xl bg-obsidian-900 p-7 text-cream-50 sm:p-10">
        <h1 className="font-serif text-4xl">QUESTIONNAIRE</h1>
        
      </section>

      <section className="premium-panel p-7 sm:p-10">
        <div className="mb-6 flex items-center justify-between gap-4">
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-obsidian-700">
            {stepTitles[step]}
          </p>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-obsidian-600">
            Step {step} of {TOTAL_STEPS}
          </p>
        </div>

        <div className="mb-6 h-2 rounded-full bg-obsidian-100">
          <div
            className="h-2 rounded-full bg-obsidian-700 transition-all"
            style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
          />
        </div>

        <form className="space-y-4" onSubmit={onStepSubmit}>
          {visibleFields.map((id) => (
            <label
              key={id}
              className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-obsidian-700"
            >
              {fields[id].label} {fields[id].required ? "*" : ""}
              {renderField(id)}
            </label>
          ))}

          <div className="mt-6 flex flex-wrap gap-3">
            {step > 1 ? (
              <button
                type="button"
                onClick={() => {
                  const prevStep = step - 1;
                  setStep(prevStep);
                  persistDraft(prevStep, done);
                }}
                className="rounded-2xl border border-obsidian-400 px-5 py-3 text-sm font-semibold text-obsidian-800 hover:bg-obsidian-50"
              >
                Back
              </button>
            ) : null}

            {!done ? (
              <button
                type="submit"
                className="rounded-2xl bg-obsidian-700 px-6 py-3 text-sm font-semibold text-cream-50 hover:bg-obsidian-600"
              >
                {step === TOTAL_STEPS ? "Submit Questionnaire" : "Next"}
              </button>
            ) : null}

            <button
              type="button"
              onClick={resetForm}
              className="rounded-2xl border border-obsidian-300 px-5 py-3 text-sm font-semibold text-obsidian-700 hover:bg-obsidian-50"
            >
              Start Over
            </button>
          </div>
        </form>

        {status ? (
          <div className="mt-5 rounded-2xl bg-obsidian-50 px-4 py-3 text-sm text-obsidian-700">
            <p>{status}</p>
          </div>
        ) : null}
      </section>
    </div>
  );
}

export default Questionnaire;
